/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2021 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: local-file-select-service.ts
 */

// This service is an implementation for FileSelectService.
// Components should invoke the abstract FileSelectService, not this specific implementation.

angular.module('prism-ui.components').service('LocalFileSelectService', function (
    this: JS5Object,
    _: JS5Object,
    $injector: JS5Object,
    $q: JS5Object,
    FileSelectService: JS5Object,
    ModalService: JS5Object,
    UtilsService: JS5Object,
) {

    //=================//
    // Lifecycle Hooks //
    //=================//

    const addLocalFileFilters = (fileSelectConfig: JS5Object, fileUploader: JS5Object) => {
        fileSelectConfig.localFilesToTryAdding = [];
        let fileExtensionFilter: JS5Object = {
            name: 'fileTypeFilter',
            errorDescription: ''
        };
        fileUploader.filters.splice(0, 0, {
            name: 'fileAddAttemptCounter',
            fn: () => {
                fileSelectConfig.localFilesToTryAdding.shift();
                return true;
            }
        });
        fileExtensionFilter.fn = (file: File, options: JS5Object) => {
            let fileExtensions: string[] = _.map(fileSelectConfig.getFileExtensions(), _.toLower);
            // TODO: AB#88420 Merge w/ select-asset-modal & aspera filter or remove completely
            // "this is ridiculous". Add other alias for these file types
            if (_.includes(fileExtensions, 'aif')) {
                fileExtensions.push('aiff');
            }
            if (_.includes(fileExtensions, 'jpg')) {
                fileExtensions.push('jpeg');
            }
            fileExtensionFilter.errorDescription = `File Type did not match [${UtilsService.filtersToPrettyString(fileExtensions)}]`;

            let fileTypeRegex = UtilsService.buildFilters(fileExtensions);
            let searchStr: string;
            if (fileSelectConfig.validateLocalMimeTypes) {
                searchStr = `.${file.type.slice(file.type.lastIndexOf('/') + 1)}`;
            } else {
                searchStr = file.name.slice(file.name.lastIndexOf('.'));
            }
            return fileTypeRegex.test(searchStr);
        };
        fileSelectConfig.localFileExtensionFilter = fileExtensionFilter;
        fileUploader.filters.push(fileSelectConfig.localFileExtensionFilter);
    };

    this.initialize = (fileSelectConfig: JS5Object) => {
        // If FileUploader is added to service constructor params,
        // many unit tests fail because "FileUploaderProvider" doesn't exist.
        // Minimize unit test breakage by getting FileUploder during run-time instead.
        let FileUploaderConstructor: JS5Function = $injector.get('FileUploader');
        let fileUploader = new FileUploaderConstructor();
        addLocalFileFilters(fileSelectConfig, fileUploader);
        fileSelectConfig.localFileUploaderConfig = fileUploader;
        fileSelectConfig.localFileDropZoneOptions = {
            openLocalWarningsPrompt: true,
        };

        fileSelectConfig.initializeUploaderConfig({fileUploaderConfig: fileUploader});
        fileUploader.onAfterAddingFile = (fileItem: JS5Object) => {
            onLocalFileAdded(fileSelectConfig, fileItem);
        }
        fileUploader.onWhenAddingFileFailed = (file: File, filter: JS5Object) => {
            onLocalFileAddFailed(fileSelectConfig, file, filter);
        }

        let origAddToQueueFunc = fileUploader.addToQueue;
        fileUploader.addToQueue = (files: any, options: JS5Object, filters: any) => {
            let startPromise: JS5Promise;
            if (_.get(options, 'openLocalWarningsPrompt')) {
                startPromise = fileSelectConfig.openWarningsPrompt();
            } else {
                startPromise = $q.when(true);
            }
            startPromise.then((canValidate: boolean) => {
                if (canValidate) {
                    fileSelectConfig.localFilesToTryAdding = fileUploader.isArrayLikeObject(files) ? Array.prototype.slice.call(files) : [files];
                    fileSelectConfig.localFileAddErrors = [];
                    fileUploader.clearQueue();
                    origAddToQueueFunc.call(fileUploader, files, options, filters);
                }
            });
        };
    };

    const clearLocalFileValue = (fileSelectConfig: JS5Object) => {
        fileSelectConfig.$element.find('input').val(null);
        if (fileSelectConfig.localFileUploaderConfig) {
            fileSelectConfig.localFileUploaderConfig.clearQueue();
        }
    };

    this.onFileChanged = (fileSelectConfig: JS5Object, newFileValue: JS5Object) => {
        if (_.get(newFileValue, 'type') !== 'LOCAL') {
            clearLocalFileValue(fileSelectConfig);
        }
    };

    this.cleanup = (fileSelectConfig: JS5Object) => {
        // Nothing to clean up
    };


    //=======================//
    // Local File Validation //
    //=======================//

    this.promptFile = (fileSelectConfig: JS5Object, options: any) => {
        let fileInput: HTMLElement;
        if (fileSelectConfig.isMultiSelect) {
            fileInput = fileSelectConfig.$element.find('input.localUploadInputMultiple')[0];
        } else {
            fileInput = fileSelectConfig.$element.find('input.localUploadInputSingle')[0];
        }
        clearLocalFileValue(fileSelectConfig);
        fileInput.click();
    };

    const onLocalFileAddFailed = (
        fileSelectConfig: JS5Object,
        file: File,
        filter: JS5Object
    ) => {
        let errors: JS5Object;
        if (filter === fileSelectConfig.localFileExtensionFilter) {
            errors = {
                source: file.name,
                errors: [{
                    description: filter.errorDescription,
                    severity: 'ERROR'
                }],
                warnings: []
            };
        } else {
            errors = fileSelectConfig.getLocalFilePreValidationErrors({file, filter});
        }
        fileSelectConfig.localFileAddErrors.push(errors);
        if (_.isEmpty(fileSelectConfig.localFilesToTryAdding)) {
            onAfterAddingAllAttempts(fileSelectConfig);
        }
    };

    const onLocalFileAdded = (fileSelectConfig: JS5Object, fileItem: JS5Object) => {
        let queueIndex = fileSelectConfig.localFileUploaderConfig.getIndexOfItem(fileItem);
        if (!fileSelectConfig.isMultiSelect && queueIndex !== 0) {
            fileSelectConfig.localFileUploaderConfig.removeFromQueue(queueIndex);
        }
        if (_.isEmpty(fileSelectConfig.localFilesToTryAdding)) {
            onAfterAddingAllAttempts(fileSelectConfig);
        }
    };

    const onAfterAddingAllAttempts = (fileSelectConfig: JS5Object) => {
        if (!_.isEmpty(fileSelectConfig.localFileAddErrors)) {
            clearLocalFileValue(fileSelectConfig);
            // ngModel value is not updated, so other components can't detect the error.
            // So, always display this modal.
            ModalService.showAssetValidationResultsModal(fileSelectConfig.localFileAddErrors, false);
            return;
        }

        let fileItems = fileSelectConfig.localFileUploaderConfig.getNotUploadedItems();
        FileSelectService.startValidation(
            fileSelectConfig,
            'LOCAL',
            fileItems
        );

        let validationPromises = fileItems.map((fileItem: JS5Object) => {
            return fileSelectConfig.validateLocalFile({
                fileItem: fileItem,
                fileUploaderConfig: fileSelectConfig.localFileUploaderConfig
            });
        });
        FileSelectService.waitForValidation(
            fileSelectConfig, $q.all(validationPromises), (validationResults: JS5Object[]) => {
                onValidationResolved(fileSelectConfig, fileItems, validationResults);
            }, (rejectedValue: any) => {
                onValidationFailed(fileSelectConfig, fileItems, rejectedValue);
            }
        );
    };

    const onValidationResolved = (
        fileSelectConfig: JS5Object,
        fileItems: JS5Object[],
        validationResults: JS5Object[]
    ) => {
        let hasErrors = false;
        let hasWarnings = false;
        let flattenedResults = _.flatten(validationResults);
        _.forEach(flattenedResults, (validationResult: JS5Object) => {
            hasErrors = hasErrors || !_.isEmpty(validationResult.errors);
            hasWarnings = hasWarnings || !_.isEmpty(validationResult.warnings);
        });
        if (hasErrors || hasWarnings) {
            onValidationFailed(fileSelectConfig, fileItems, validationResults);
            if (fileSelectConfig.canShowValidationErrorModal()) {
                ModalService.showAssetValidationResultsModal(
                    flattenedResults, !hasErrors, () => {
                        onValidationCompleted(fileSelectConfig, fileItems, validationResults);
                    });
            }
        } else {
            onValidationCompleted(fileSelectConfig, fileItems, validationResults);
        }
    };

    const onValidationCompleted = (
        fileSelectConfig: JS5Object,
        fileItems: JS5Object[],
        validationResults: JS5Object[]
    ) => {
        FileSelectService.completeValidation(
            fileSelectConfig,
            'LOCAL',
            fileItems,
            validationResults
        );
    };

    const onValidationFailed = (
        fileSelectConfig: JS5Object,
        fileItems: JS5Object[],
        results: any
    ) => {
        FileSelectService.failValidation(
            fileSelectConfig,
            'LOCAL',
            fileItems,
            results
        );
    };
});


//=================//
// Lifecycle Hooks //
//=================//

// Load this service when app is initialized
angular.module('prism-ui.components').run((
    FileSelectService: JS5Object,
    LocalFileSelectService: JS5Object,
) => {
    FileSelectService.registerFileSelectServiceImplementation('LOCAL', LocalFileSelectService);
});
